import React, { ReactNode, useEffect } from 'react';
import { Form, Button, Table } from 'react-bootstrap';
import { Link, useForm, usePage } from '@inertiajs/react';

import AuthedLayout from '../../Layouts/Authed';
import LhCarrier from '../../Data/LhCarrier';
import Valuation from '../../Data/Valuation';
import LhCarrierRow from './LhCarriers/LhCarrierRow';
import { getEventValue, replaceNullWithEmpty } from './Helpers/RepeatingForms';
import { range } from '../../utils';
import LhCarrierOverridesRow from './LhCarriers/LhCarrierOverridesRow';
import FormHeader from '../../Components/Forms/FormHeader';
import InstructionsPanel from '../../Components/Forms/InstructionsPanel';
import formatCurrency from './Helpers/FormatCurrency';
import AutosaveStatus from '../../Components/Forms/AutosaveStatus';
import MarkComplete from '../../Components/Forms/MarkComplete';
import useValuationStepAutosave from './Helpers/useValuationStepAutosave';

export interface LhCarrierAdditionalInfo {
	overrides: Record<number, number | null>;
}

interface Props {
	valuation: Valuation;
	lhCarriers: (LhCarrier & LhCarrierAdditionalInfo)[];
	maxLhCarriersPerValuation: number;
	currentYear: number;
}

interface FormData {
	lhCarriers: (Partial<LhCarrier> & LhCarrierAdditionalInfo)[];
	mark_complete: boolean;
}

const LhCarriers = (props: Props) => {
	const { errors, readOnly } = usePage<any>().props;

	const { data, setData, post, processing } = useForm<FormData>({
		lhCarriers: replaceNullWithEmpty(props.lhCarriers) ?? [],
		mark_complete: props.valuation.completed_steps?.includes('lh-carriers') ?? false,
	});
	const autosaveStatus = useValuationStepAutosave('lh-carriers', props.valuation.id, data);

	const submit = (event: any) => {
		event.preventDefault();
		post(`/valuation-form/${props.valuation.id}/lh-carriers`);
	};

	const updateIndex = (event: React.ChangeEvent<any>, index: number) => {
		const newData = JSON.parse(JSON.stringify(data));
		newData.lhCarriers[index][event.target.name] = getEventValue(event);
		setData(newData);
	};

	const updateOverride = (
		index: number,
		year: string,
		event: React.ChangeEvent<any>
	) => {
		const newData: any = { ...data };
		newData.lhCarriers[index].overrides[year] = getEventValue(event);
		setData(newData);
	};

	const summedTotalAnnualPrem = data.lhCarriers.reduce(
		(accumulator, currentValue) =>
			accumulator + (currentValue?.total_annual_premium || 0),
		0
	);

	const summedAnnualAgencyComm = data.lhCarriers.reduce(
		(accumulator, currentValue) =>
			accumulator + (currentValue?.annual_agency_commission || 0),
		0
	);

	const totalPremComparison =
		props.valuation.annual_total_premium > 0
			? (
					(summedTotalAnnualPrem /
						props.valuation.annual_total_premium) *
					100
			  ).toFixed(2)
			: 0;

	const totalCommComparison =
		props.valuation.annual_total_commission > 0
			? (
					(summedAnnualAgencyComm /
						props.valuation.annual_total_commission) *
					100
			  ).toFixed(2)
			: 0;

	const yearTotal = (year: number) =>
		formatCurrency(
			data.lhCarriers.reduce(
				(accumulator, currentValue) =>
					accumulator + (currentValue?.overrides?.[year] || 0),
				0
			)
		);

	useEffect(() => {
		scrollTo(0,0);
	}, []);

	return (
		<div>
			<FormHeader
				agencyName={props.valuation.agency_name}
				effectiveDate={props.valuation.effective_date}
				pageName='Top 5 Group / L&H Carriers'
			/>
			<InstructionsPanel>
				Use this tab to record your agency's top 5 life and or health carrier premium and commission amounts. Check the box when complete or if it is not applicable.
			</InstructionsPanel>
			<Form onSubmit={submit} className='valuationForm'>
				<fieldset disabled={readOnly}>
				<Table striped borderless hover responsive>
					<thead>
						<tr>
							<th
								id='lhCarrierNum'
								className='cell-xs sticky-cell'
							>
								#
							</th>
							<th id='carrier_name'>Carrier Name</th>
							<th id='total_annual_premium'>
								Total Annual Premium
							</th>
							<th id='percent_of_total_agency_premiums'>
								Percent Of Total Agency Premiums
							</th>
							<th id='annual_agency_commission'>
								Annual Agency Commission
							</th>
							<th id='percent_of_total_agency_commissions'>
								Percent Of Total Agency Commissions
							</th>
						</tr>
					</thead>
					<tbody>
						{data.lhCarriers.map((lhCarrier, index) => (
							<LhCarrierRow
								key={`lhCarrier-${index}`}
								lhCarrier={lhCarrier}
								annual_total_premium={
									props.valuation.annual_total_premium
								}
								annual_total_commission={
									props.valuation.annual_total_commission
								}
								index={index}
								errors={errors}
								updateIndex={updateIndex}
							/>
						))}
					</tbody>
					<tfoot>
						<tr>
							<th />
							<th />
							<th>{formatCurrency(summedTotalAnnualPrem)}</th>
							<th>{totalPremComparison}%</th>
							<th>{formatCurrency(summedAnnualAgencyComm)}</th>
							<th>{totalCommComparison}%</th>
						</tr>
					</tfoot>
				</Table>

				<h1 className='h4'>Override $'s Received by Year</h1>
				<Table striped borderless hover responsive>
					<thead>
						<tr>
							<th
								id='lhCarrierOverrideNum'
								className='cell-xs sticky-cell'
							>
								#
							</th>
							<th id='carrier_name_override'>Carrier Name</th>
							{range(
								props.currentYear - 5,
								props.currentYear
							).map((year) => (
								<th key={year}>{year}</th>
							))}
						</tr>
					</thead>
					<tbody>
						{data.lhCarriers.map((lhCarrier, index) => (
							<LhCarrierOverridesRow
								key={`lhCarrier-overrides-${index}`}
								currentYear={props.currentYear}
								lhCarrier={lhCarrier}
								index={index}
								errors={errors}
								updateOverride={updateOverride}
							/>
						))}
					</tbody>
					<tfoot>
						<tr>
							<th />
							<th />
							{range(
								props.currentYear - 5,
								props.currentYear
							).map((year) => (
								<th key={year}>{yearTotal(year)}</th>
							))}
						</tr>
					</tfoot>
				</Table>
				</fieldset>

				<div className='d-flex justify-content-between align-items-center'>
					<Button
						as={Link as any}
						href={`/valuation-form/${props.valuation.id}/pc-carriers`}
						variant='outline-primary'
						className='mt-4'
					>
						Back
					</Button>
					<div className='d-flex align-items-center'>
						{!readOnly && <AutosaveStatus status={autosaveStatus} />}
						{!readOnly && (
							<MarkComplete
								checked={data.mark_complete}
								onChange={(checked) => setData('mark_complete', checked)}
								className='mt-4 ms-4'
							/>
						)}
						{readOnly ? (
							<Button
								as={Link as any}
								href={`/valuation-form/${props.valuation.id}/producer-bobs`}
								className='mt-4 ms-4'
							>
								Next
							</Button>
						) : (
							<Button type='submit' className='mt-4 ms-4'>
								Next
							</Button>
						)}
					</div>
				</div>
			</Form>
		</div>
	);
};

LhCarriers.layout = (page: ReactNode) => <AuthedLayout>{page}</AuthedLayout>;

export default LhCarriers;
